/**
 * Who a charge's money is for.
 *
 * The product was first shaped around an assumption that turned out to be the opposite of how
 * this trade works: that the agency's income is a percentage cut of the landlord's rent. It is
 * not. **The rent is the landlord's, whole and untouched**, and the agency's income is the agency
 * fee the tenant pays on top of it — the way a tax is added rather than the way a commission is
 * deducted. The agency collects everything and distributes it: the rent and usually the caution
 * deposit to the landlord, its own fee to itself, the agreement fee to the lawyer who drew the
 * agreement.
 *
 * So a charge is not only an amount and a label. It carries **who this money is for**, and a
 * payout run stops being "rent minus deductions" and becomes "here is what was collected, and here
 * is whose each part of it is" — which is what actually happens on the desk.
 *
 * ## The list belongs to the agency
 *
 * There is no enum of beneficiaries here, and there never can be: the third party on a letting is
 * a lawyer at one agency, a surveyor at another, an estate association at a third. The list is a
 * per-agency table, added to **at the point the choice is made**, because the moment somebody
 * needs a new beneficiary is the moment they are recording a charge, and sending them to a
 * settings screen to come back is how a form gets abandoned.
 *
 * ## The one property that decides everything downstream
 *
 * **`accrues` — whether this beneficiary produces a payout obligation — is set when the
 * beneficiary is added, and is never inferred from its name or its kind at read time.**
 *
 * That is the whole design, and it is worth saying why it is not a guess from the kind. A landlord
 * is a party with a ledger: money collected for them accumulates and a payout run pays it out. A
 * lawyer, in the operator's words, is *"just a line of statement"* — the destination is recorded
 * and no obligation is tracked, because the agency hands over the agreement fee at the point of
 * the letting and there is nothing to accumulate. The agency itself is the third case: money that
 * has arrived and is going nowhere.
 *
 * Those are three *usual* answers, not three rules. An agency that does hold a lawyer's fees and
 * settles them monthly is describing an ordinary arrangement, and a model that decided for them —
 * "a contact never accrues" — would be telling them their own books are wrong. The kind supplies
 * the default when the beneficiary is created; the column is what is read afterwards.
 */

/**
 * What a beneficiary *is*, which decides what record stands behind it.
 *
 * - `agency` — the agency itself. Exactly one per agency, created the first time a charge needs
 *   it. It has no record behind it because it is the record: this is money that arrived and is
 *   staying.
 * - `client` — somebody already on the client list, named by `partyId`. A landlord is the ordinary
 *   case, and it is the one kind that can be reconciled against a party's own ledger, which is why
 *   a payout is still keyed on the party as well as on the beneficiary.
 * - `contact` — a light record of its own: a name and how to reach them, no more. **A lawyer is
 *   not forced into the client list**, which was an explicit instruction: the client list is
 *   people the agency lets to and lets for, and filling it with every professional who ever drew
 *   an agreement makes it unusable for the thing it is for.
 */
export const BENEFICIARY_KINDS = ["agency", "client", "contact"] as const;
export type BeneficiaryKind = (typeof BENEFICIARY_KINDS)[number];

/**
 * Whether a newly added beneficiary of this kind is presumed to accumulate a balance.
 *
 * A **default offered at the point of creation and stored**, never a rule applied at read time.
 * The distinction matters: the moment this were consulted when reading a charge, an agency that
 * had deliberately said "we do hold this lawyer's fees" would find the software quietly
 * disagreeing with them on every statement.
 */
export function accruesByDefault(kind: BeneficiaryKind): boolean {
  return kind === "client";
}

/**
 * Which beneficiary a charge is *for* when nobody has said.
 *
 * Three roles rather than an id, because this is a rule about charge types and the ids belong to
 * an agency. `resolveBeneficiary` on the API side turns a role into the agency's own row.
 *
 * - `landlord` — the owner of the unit the charge is against. Rent, and the caution deposit.
 * - `agency` — the agency itself.
 * - `unassigned` — nothing can be assumed. A legal fee belongs to whoever drew the agreement and
 *   the software has no way to know who that was.
 */
export type BeneficiaryRole = "landlord" | "agency" | "unassigned";

/**
 * A charge line resolved to whose money it is.
 *
 * Deliberately not "the landlord's share": there are three or more parties in every letting and
 * naming one of them in the type would be the same mistake the commission model made.
 */
export interface BeneficiaryShare {
  /** The agency's own beneficiary row, or null where a line predates the field and had no default. */
  beneficiaryId: string | null;
  amount: number;
}

/**
 * Split money that settled an invoice across the beneficiaries of that invoice's lines.
 *
 * ## Why an apportionment rather than an order
 *
 * An allocation is the only record that says which invoice a given minor unit settled — that is
 * ADR-0099 and it is not in question. What it does *not* say is which **line** of that invoice, and
 * an invoice for a new letting is routinely a year's rent, an agency fee and a caution deposit
 * belonging to three different people. A part payment against it has to be divided somehow.
 *
 * **Pro rata is the only division nobody agreed otherwise.** The alternatives are all a policy
 * somebody would have to be told about: rent first favours the landlord, fees first favours the
 * agency, and either would mean a tenant who paid half their commencement invoice had, without
 * being asked, decided which of three people got paid. Dividing it in the proportions of the
 * invoice itself is the one answer that treats the payment as what it is — a part of the whole
 * thing — and it is what an agency does on paper.
 *
 * `apportion` in `payouts.ts` does the arithmetic and guarantees the shares sum **exactly** to the
 * amount allocated, with the odd minor unit going to the largest share. Nothing is lost and
 * nothing is invented; a statement that is one unit out is not a rounding artefact to a landlord.
 *
 * Lines are grouped by beneficiary first, so an invoice carrying two rent lines for one owner
 * produces one share rather than two that have to be added up again later.
 */
export function shareByBeneficiary(
  allocated: number,
  lines: ReadonlyArray<{ beneficiaryId: string | null; amount: number }>,
  apportionFn: (total: number, weights: readonly number[]) => number[],
): BeneficiaryShare[] {
  const order: Array<string | null> = [];
  const totals = new Map<string | null, number>();
  for (const line of lines) {
    if (line.amount <= 0) continue;
    const key = line.beneficiaryId ?? null;
    if (!totals.has(key)) order.push(key);
    totals.set(key, (totals.get(key) ?? 0) + line.amount);
  }

  /* An invoice with no lines at all, or only zero-value ones. There is nothing to divide the
     money in the proportions of, so it is reported as belonging to nobody rather than guessed at —
     the same decision the run makes about credit held against no invoice. */
  if (!order.length) return allocated > 0 ? [{ beneficiaryId: null, amount: allocated }] : [];

  const weights = order.map((key) => totals.get(key) ?? 0);
  const shares = apportionFn(allocated, weights);
  return order.map((beneficiaryId, index) => ({ beneficiaryId, amount: shares[index] })).filter((share) => share.amount !== 0);
}
